import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import GridViewIcon from '@mui/icons-material/GridView';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { useUserRolePermissions } from 'hooks';
import { useParams } from 'react-router-dom';
import FiltersDropdown from 'feature/dispatching/components/FiltersDropdown';
import { useViewLoadsSummaryByDriverQuery } from '../loadsEndpoints';

const containerStyles = {
  backgroundColor: 'surface.light',
  ml: 1,
  pb: 3,
  position: 'sticky',
  top: 48,
  zIndex: 2,
};

const cardStyles = {
  backgroundColor: 'surface.light',
  boxShadow: 'none',
  display: 'flex',
  justifyContent: 'space-between',
};

const DriverHeader = ({
  appliedFilters,
  children,
  filtersPanel,
  layout,
  onCreate,
  onLayoutChange,
  setFilters,
}) => {
  const { isDispatchingManage } = useUserRolePermissions();
  const filtersRef = useRef();
  const [filtersOpened, setFiltersOpen] = useState(false);
  const { viewById } = useParams();
  const viewByDriverId = viewById;

  const openFilters = R.compose(setFiltersOpen, R.T);
  const closeFilters = R.compose(setFiltersOpen, R.F);

  // prettier-ignore
  const itemFilter = R.compose(
    R.equals(viewByDriverId),
    R.prop('driverId'),
  );
  const { driverQuery = {}, isSuccess } = useViewLoadsSummaryByDriverQuery(undefined, {
    selectFromResult: result => ({
      driverQuery: result.data?.find(itemFilter),
      isLoading: result.isLoading,
      isSuccess: result.isSuccess,
    }),
    skip: R.isNil(viewById),
  });
  const { badgeIds = [] } = driverQuery;

  const handleLayoutChange = R.compose(onLayoutChange, R.nthArg(1));

  const handleResetFilter = R.compose(setFilters, R.assoc);

  // prettier-ignore
  const titleDisplay = isSuccess 
    ? `${driverQuery.lastName}, ${driverQuery.firstName}` 
    : 'Loading...';

  // prettier-ignore
  const filtersIcon = filtersOpened
    ? <ExpandLessRoundedIcon position="right" variant="buttonsChild" />
    : <ExpandMoreRoundedIcon position="right" variant="buttonsChild" />

  // prettier-ignore
  // renderBadges :: [String] -> String
  const renderBadges = R.compose(
    R.when(
      xs => R.gt(R.length(xs), 20), 
      R.o(R.concat(R.__, '...'), R.take(17))
    ),
    R.join(', ')
  );

  return (
    <Box sx={containerStyles}>
      <Card sx={cardStyles}>
        <CardHeader
          slotProps={{
            subheader: { variant: 'body2' },
            title: { variant: 'h5' },
          }}
          subheader={renderBadges(badgeIds)}
          title={R.toUpper(titleDisplay)}
        />
        <CardContent sx={{ alignItems: 'center', display: 'flex', gap: 4 }}>
          <Button
            disabled={!isSuccess}
            onClick={openFilters}
            onClose={closeFilters}
            ref={filtersRef}
            variant="text"
          >
            Filters
            {Boolean(appliedFilters.length) && (
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  height: 20,
                  justifyContent: 'center',
                  ml: 0.5,
                  width: 22,
                }}
              >
                <Typography variant="caption">{appliedFilters.length}</Typography>
              </Box>
            )}
            {filtersIcon}
          </Button>

          <ToggleButtonGroup exclusive onChange={handleLayoutChange} value={layout}>
            <ToggleButton sx={{ borderRadius: 18, p: 1, pl: 1.5, width: 48 }} value="grid">
              <GridViewIcon />
            </ToggleButton>
            <ToggleButton sx={{ borderRadius: 18, p: 1, pr: 1.5, width: 48 }} value="list">
              <FormatListBulletedIcon />
            </ToggleButton>
          </ToggleButtonGroup>

          {isDispatchingManage && <Button onClick={onCreate}>New Load</Button>}
        </CardContent>

        <FiltersDropdown anchorEl={filtersRef.current} onClose={closeFilters} open={filtersOpened}>
          {filtersPanel}
        </FiltersDropdown>
      </Card>
      {children}
      {Boolean(appliedFilters.length) && (
        <Stack direction="row" spacing={1} sx={{ mt: 3, mx: 2 }}>
          {appliedFilters.map(({ field, label, value, clearValue }) => (
            <Chip
              key={value}
              label={`${label}: ${value}`}
              onDelete={() => handleResetFilter(field, clearValue)}
              sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
              variant="outlined"
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

DriverHeader.propTypes = {
  appliedFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  children: PropTypes.element.isRequired,
  filtersPanel: PropTypes.element.isRequired,
  layout: PropTypes.oneOf(['grid', 'list']).isRequired,
  onCreate: PropTypes.func.isRequired,
  onLayoutChange: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
};

export default DriverHeader;
